import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { inputCls, ghostBtnCls, primaryBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const rup = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

export default function LabDueReport() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ from_date: monthStart(), to_date: today(), reg_no: "", q: "" });

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get("/lab/business/due", { params: { from_date: f.from_date || undefined, to_date: f.to_date || undefined, reg_no: f.reg_no || undefined, q: f.q || undefined } }); setData(data); }
    catch (e) { setError(apiError(e)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  if (!can("lab.reports.view")) return <NoAccess what="the due report" />;

  return (
    <div data-testid="lab-due-report-page">
      <PageHeader overline="Business" title="Due Report" subtitle="Outstanding balances across bills. Collect payment or Accept Due from the bill." />
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="panel p-4 mb-5 grid grid-cols-2 md:grid-cols-5 gap-3">
        <input type="date" className={inputCls} value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="due-from" />
        <input type="date" className={inputCls} value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="due-to" />
        <input className={inputCls} placeholder="Reg. no." value={f.reg_no} onChange={(e) => setF({ ...f, reg_no: e.target.value })} data-testid="due-regno" />
        <input className={inputCls} placeholder="Patient / mobile" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="due-q" />
        <div className="flex gap-2"><button className={primaryBtnCls} data-testid="due-search">Search</button><button type="button" className={ghostBtnCls} onClick={() => window.print()}>Print</button></div>
      </form>
      {error && <ErrorState message={error} />}
      {!data ? <Loading /> : (
        <>
          <div className="panel px-4 py-2 mb-4 inline-block text-sm">Total due: <span className="font-semibold text-amber-700" data-testid="due-total">{rup(data.total_due)}</span> across {data.count} bills</div>
          <Table testid="lab-due-table" columns={["Reg", "Date", "Patient", "Mobile", "Referrer", "Total", "Paid", "Discount", "Due", ""]}
            rows={data.items} empty={<EmptyState title="No dues" subtitle="No outstanding balances in this range." testid="lab-due-empty" />}
            renderRow={(b) => (
              <tr key={b.bill_id} data-testid={`due-row-${b.bill_id}`}>
                <Td className="font-mono">{b.reg_no}</Td><Td className="text-xs text-slate-500">{(b.created_at || "").slice(0, 10)}</Td>
                <Td className="font-medium text-slate-900">{b.patient_name}</Td><Td className="text-slate-600">{b.patient_phone}</Td>
                <Td className="text-slate-600">{b.referrer_name || "—"}</Td><Td>{rup(b.net_total)}</Td><Td>{rup(b.paid)}</Td>
                <Td>{rup(b.total_discount)}</Td><Td className="text-amber-700 font-medium">{rup(b.due)}</Td>
                <Td><Link to={`/app/lab/bills/${b.bill_id}`} className="underline text-sm" data-testid={`due-view-${b.bill_id}`}>View bill</Link></Td>
              </tr>
            )} />
        </>
      )}
    </div>
  );
}
